import { ConfirmationDialogRaw } from "@/controls/modals/ConfirmationDialogRaw";
import {
	ErrorModal,
	HelmetDexteel,
	useHasPermission,
} from "@dexteel/mesf-core";
import AddIcon from "@mui/icons-material/Add";
import CancelIcon from "@mui/icons-material/Cancel";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PublishIcon from "@mui/icons-material/Publish";
import SaveIcon from "@mui/icons-material/Save";
import UndoIcon from "@mui/icons-material/Undo";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import {
	Box,
	Button,
	Chip,
	CircularProgress,
	FormControl,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { useEffect, useState } from "react";

import {
	TENSILE_REQUIREMENT_INITIAL_VALUES,
	TensileRequirementByGrade,
	TensileRequirementVersion,
} from "../../models/TensileRequirementByGrade";
import {
	confirmTensileRequirementVersion,
	deleteTensileRequirementVersion,
	getTensileRequirementVersions,
	getTensileRequirementsByGrade,
	unscheduleTensileRequirementVersion,
	upsertTensileRequirements,
} from "../../repositories/TensileRequirementByGradeRepository";
import { ConfirmVersionModal } from "../../versions/ConfirmVersionModal";
import { NewVersionModal } from "../../versions/NewVersionModal";
import { StatusIcon, VERSION_STATUS } from "../../versions/versionStatus";
import { TensileRequirementsByGradeTable } from "./components/TensileRequirementsByGradeTable";

const versionLabel = (version: TensileRequirementVersion) =>
	version.StatusCode === VERSION_STATUS.DRAFT
		? "Draft"
		: `Rev ${version.Rev} - ${new Date(
				version.EffectiveDate ?? new Date(),
			).toLocaleDateString()}`;

const currentVersionId = (versions: TensileRequirementVersion[]) =>
	versions
		.filter((v) => v.StatusCode === VERSION_STATUS.PUBLISHED)
		.sort(
			(a, b) =>
				new Date(b.EffectiveDate ?? new Date()).getTime() -
				new Date(a.EffectiveDate ?? new Date()).getTime(),
		)[0]?.TensileRequirementVersionId ?? null;

const VersionOption = ({
	version,
	isCurrent,
}: {
	version: TensileRequirementVersion;
	isCurrent: boolean;
}) => (
	<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
		<StatusIcon
			status={version.StatusCode}
			isCurrent={isCurrent}
			date={
				version.EffectiveDate == null
					? undefined
					: new Date(version.EffectiveDate).toLocaleDateString()
			}
		/>
		{versionLabel(version)}
	</Box>
);

export const useTensileRequirementVersions = () =>
	useQuery<TensileRequirementVersion[], Error>({
		queryKey: ["tensileRequirementVersions"],
		queryFn: ({ signal }) => getTensileRequirementVersions(signal),
	});

export const useTensileRequirements = (versionId: number | null) =>
	useQuery<TensileRequirementByGrade[], Error>({
		queryKey: ["tensileRequirementsByGrade", versionId],
		queryFn: ({ signal }) =>
			getTensileRequirementsByGrade({ versionId }, signal),
		enabled: versionId != null,
	});

const formatRevision = (version: TensileRequirementVersion | null) =>
	version?.Rev == null ? "Rev pending" : `Rev ${version.Rev}`;

type Props = {
	readOnly?: boolean;
};

export const TensileRequirementsPage = ({ readOnly = false }: Props) => {
	const queryClient = useQueryClient();
	const canEditPublished = useHasPermission()(
		"ui.configuration.tensile.requirements.edit-published",
	);

	const [error, setError] = useState("");
	const [selectedVersionId, setSelectedVersionId] = useState<number | null>(
		null,
	);
	const [isEditMode, setIsEditMode] = useState(false);
	const [editVersionId, setEditVersionId] = useState<number | null>(null);
	const [editComments, setEditComments] = useState<string>("");
	const [editRows, setEditRows] = useState<TensileRequirementByGrade[]>([]);
	const [newVersionOpen, setNewVersionOpen] = useState(false);
	const [confirmVersionOpen, setConfirmVersionOpen] = useState(false);
	const [confirm, setConfirm] = useState<"changeToDraft" | "delete" | null>(
		null,
	);

	const {
		data: allVersions,
		isLoading: versionsLoading,
		error: versionsError,
		isError: isVersionsError,
	} = useTensileRequirementVersions();

	const versions = readOnly
		? allVersions?.filter((v) => v.StatusCode !== VERSION_STATUS.DRAFT)
		: allVersions;
	const orderedVersions = [...(versions ?? [])].sort(
		(a, b) =>
			Number(b.StatusCode === VERSION_STATUS.DRAFT) -
			Number(a.StatusCode === VERSION_STATUS.DRAFT),
	);

	const defaultVersionId =
		versions?.find((v) => v.StatusCode === VERSION_STATUS.PUBLISHED)
			?.TensileRequirementVersionId ??
		versions?.[0]?.TensileRequirementVersionId ??
		null;

	const versionId = selectedVersionId ?? defaultVersionId;
	const currentId = currentVersionId(versions ?? []);
	const selectedVersion =
		versions?.find((v) => v.TensileRequirementVersionId === versionId) ?? null;

	const isDraft =
		!readOnly && selectedVersion?.StatusCode === VERSION_STATUS.DRAFT;
	const isScheduled =
		!readOnly && selectedVersion?.StatusCode === VERSION_STATUS.SCHEDULED;
	const isLatestPublished =
		!readOnly &&
		selectedVersion?.StatusCode === VERSION_STATUS.PUBLISHED &&
		selectedVersion.IsLatest;
	const isEditingPublished =
		isEditMode && selectedVersion?.StatusCode === VERSION_STATUS.PUBLISHED;
	const hasTestRuns = (selectedVersion?.TestRunCount ?? 0) > 0;
	const draftVersionId =
		(allVersions ?? []).find((v) => v.StatusCode === VERSION_STATUS.DRAFT)
			?.TensileRequirementVersionId ?? null;
	const hasDraft = draftVersionId != null;
	const hasPendingVersion = (allVersions ?? []).some(
		(v) => v.StatusCode !== VERSION_STATUS.PUBLISHED,
	);
	const confirmedVersions = (allVersions ?? []).filter((v) => v.IsConfirmed);
	const maxRev = confirmedVersions.reduce(
		(max, v) => Math.max(max, v.Rev ?? 0),
		0,
	);
	const lastEffectiveDate = confirmedVersions.reduce<Date | null>(
		(last, v) =>
			v.EffectiveDate != null &&
			(last == null || new Date(v.EffectiveDate) > new Date(last))
				? v.EffectiveDate
				: last,
		null,
	);

	const {
		data: rows,
		isLoading: rowsLoading,
		error: rowsError,
		isError: isRowsError,
	} = useTensileRequirements(versionId);

	const invalidateVersions = async () => {
		await queryClient.invalidateQueries({
			queryKey: ["tensileRequirementVersions"],
		});
		await queryClient.invalidateQueries({
			queryKey: ["tensileRequirementsByGrade"],
		});
	};

	const saveMutation = useMutation({
		mutationFn: upsertTensileRequirements,
		onSuccess: async (savedId: number) => {
			await invalidateVersions();
			setSelectedVersionId(savedId);
			setIsEditMode(false);
			setEditRows([]);
		},
		onError: (e: Error) => setError(e.message),
	});

	const confirmMutation = useMutation({
		mutationFn: confirmTensileRequirementVersion,
		meta: { successMessage: "Specification confirmed" },
		onSuccess: async () => {
			await invalidateVersions();
			setConfirmVersionOpen(false);
		},
		onError: (e: Error) => setError(e.message),
	});

	const unscheduleMutation = useMutation({
		mutationFn: unscheduleTensileRequirementVersion,
		meta: { successMessage: "Revision changed to Draft" },
		onSuccess: async () => {
			await invalidateVersions();
		},
		onError: (e: Error) => setError(e.message),
	});

	const deleteMutation = useMutation({
		mutationFn: deleteTensileRequirementVersion,
		meta: { successMessage: "Draft deleted" },
		onSuccess: async () => {
			setSelectedVersionId(null);
			await invalidateVersions();
		},
		onError: async (e: Error) => {
			setError(e.message);
			await invalidateVersions();
		},
	});

	const handleRowUpdate = (
		rowIndex: number,
		updates: Partial<TensileRequirementByGrade>,
	) => {
		setEditRows((prev) => {
			const updated = [...prev];
			if (updated[rowIndex]) {
				updated[rowIndex] = { ...updated[rowIndex], ...updates };
			}
			return updated;
		});
	};

	const copyOfRows = () =>
		(rows ?? []).map((row) => ({
			...row,
			TensileRequirementsByGradeId: null,
			TensileRequirementVersionId: null,
		}));

	const startEditing = () => {
		if (!rows) return;

		setEditVersionId(versionId);
		setEditComments(selectedVersion?.Comments ?? "");
		setEditRows(copyOfRows());
		setIsEditMode(true);
	};

	const handleNewVersion = (comments: string | null) => {
		if (!rows) return;

		saveMutation.mutate(
			{ versionId: null, comments, rows: copyOfRows() },
			{
				onSuccess: () => {
					setNewVersionOpen(false);
					enqueueSnackbar(
						"Draft created — edit the requirements, then Confirm when ready.",
						{ variant: "success" },
					);
				},
			},
		);
	};

	const handleAddRow = () => {
		setEditRows((prev) => [...prev, { ...TENSILE_REQUIREMENT_INITIAL_VALUES }]);
	};

	const handleRowDelete = (rowIndex: number) => {
		setEditRows((prev) => prev.filter((_, i) => i !== rowIndex));
	};

	const handleCancel = () => {
		setIsEditMode(false);
		setEditRows([]);
		setEditVersionId(null);
		setEditComments("");
	};

	const handleSave = () => {
		for (const row of editRows) {
			if (
				!row.SWV ||
				row.SWV < 1 ||
				row.SWV > 999 ||
				!Number.isInteger(row.SWV)
			) {
				setError("Every row needs a whole SWV between 1 and 999.");
				return;
			}
		}

		const ranges = [
			["Yield", "YieldMin", "YieldMax"],
			["Tensile", "TensileMin", "TensileMax"],
			["%RA", "PercentRAMin", "PercentRAMax"],
			["Impact", "ImpactMin", "ImpactMax"],
		] as const;
		for (const row of editRows) {
			for (const [label, minKey, maxKey] of ranges) {
				const min = row[minKey];
				const max = row[maxKey];
				if (min != null && max != null && min > max) {
					setError(
						`SWV ${row.SWV}: the ${label} minimum is above its maximum.`,
					);
					return;
				}
			}
		}

		const swvSet = new Set<number>();
		for (const row of editRows) {
			if (swvSet.has(row.SWV ?? 0)) {
				setError("SWV values must be unique.");
				return;
			}
			swvSet.add(row.SWV ?? 0);
		}

		saveMutation.mutate(
			{
				versionId: editVersionId,
				comments: editComments || null,
				rows: editRows,
			},
			{
				onSuccess: () =>
					enqueueSnackbar(
						isEditingPublished ? "Published revision updated" : "Draft saved",
						{ variant: "success" },
					),
			},
		);
	};

	const handleConfirmClose = (value?: string) => {
		const action = confirm;
		setConfirm(null);
		if (value !== "OK" || versionId == null) return;
		if (action === "changeToDraft") unscheduleMutation.mutate({ versionId });
		if (action === "delete") deleteMutation.mutate({ versionId });
	};

	useEffect(() => {
		if (isVersionsError && versionsError) {
			setError(versionsError.message);
		}
		if (isRowsError && rowsError) {
			setError(rowsError.message);
		}
	}, [isVersionsError, versionsError, isRowsError, rowsError]);

	const displayedRows = isEditMode ? editRows : rows || [];
	const isBusy =
		saveMutation.isPending ||
		confirmMutation.isPending ||
		unscheduleMutation.isPending ||
		deleteMutation.isPending;

	const backToDraftButton = (
		<Tooltip
			title={
				hasDraft
					? "There is already a draft. Edit or delete it first."
					: hasTestRuns
						? "Mechanical test results were received under this revision, so it stays as history"
						: ""
			}
		>
			<span>
				<Button
					variant="outlined"
					size="small"
					startIcon={<UndoIcon />}
					onClick={() => setConfirm("changeToDraft")}
					data-test-id="change-to-draft-button"
					disabled={isBusy || hasDraft || hasTestRuns}
				>
					Back to Draft
				</Button>
			</span>
		</Tooltip>
	);

	return (
		<>
			{!readOnly && <HelmetDexteel title="Tensile Requirements by Grade" />}
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					height: readOnly ? "78vh" : "85vh",
					p: 1,
					gap: 2,
				}}
			>
				{!readOnly && (
					<Typography variant="h5" fontWeight={600}>
						Tensile Requirements by Grade
					</Typography>
				)}

				<Box
					sx={{
						display: "flex",
						gap: 2,
						alignItems: "center",
						justifyContent: "space-between",
						flexWrap: "wrap",
					}}
				>
					<Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
						{!readOnly && (
							<FormControl size="small" sx={{ minWidth: 240 }}>
								<InputLabel id="tensile-version-label">Version</InputLabel>
								<Select
									labelId="tensile-version-label"
									label="Version"
									data-test-id="version-selector"
									value={versionId ?? ""}
									disabled={versionsLoading || isEditMode}
									onChange={(e) => setSelectedVersionId(Number(e.target.value))}
									sx={{
										backgroundColor: (theme) => theme.palette.background.paper,
									}}
									renderValue={(value) => {
										const version = versions?.find(
											(v) => v.TensileRequirementVersionId === value,
										);
										return version ? (
											<VersionOption
												version={version}
												isCurrent={
													version.TensileRequirementVersionId === currentId
												}
											/>
										) : null;
									}}
								>
									{orderedVersions?.map((v) => (
										<MenuItem
											key={v.TensileRequirementVersionId}
											value={v.TensileRequirementVersionId}
										>
											<VersionOption
												version={v}
												isCurrent={v.TensileRequirementVersionId === currentId}
											/>
										</MenuItem>
									))}
								</Select>
							</FormControl>
						)}

						{isDraft && (
							<Chip
								label="Draft"
								color="warning"
								size="small"
								data-test-id="draft-chip"
							/>
						)}
						{isScheduled && (
							<Chip
								label="Scheduled"
								color="info"
								size="small"
								data-test-id="scheduled-chip"
							/>
						)}
						{isEditingPublished && (
							<Chip
								label="Editing published revision"
								color="warning"
								size="small"
								data-test-id="editing-published-chip"
							/>
						)}
					</Box>

					{!readOnly && (
						<Box sx={{ display: "flex", gap: 1 }}>
							{isEditMode ? (
								<>
									<Button
										variant="outlined"
										size="small"
										startIcon={<AddIcon />}
										onClick={handleAddRow}
										data-test-id="add-row-button"
										sx={{
											backgroundColor: (theme) =>
												theme.palette.background.paper,
										}}
									>
										Add Row
									</Button>
									<Button
										variant="outlined"
										size="small"
										color="error"
										startIcon={<CancelIcon />}
										onClick={handleCancel}
										data-test-id="cancel-edit-button"
										disabled={saveMutation.isPending}
										sx={{
											backgroundColor: (theme) =>
												theme.palette.background.paper,
										}}
									>
										Cancel
									</Button>
									<Button
										variant="contained"
										size="small"
										startIcon={
											saveMutation.isPending ? (
												<CircularProgress size={16} color="inherit" />
											) : (
												<SaveIcon />
											)
										}
										onClick={handleSave}
										data-test-id="save-draft-button"
										disabled={saveMutation.isPending}
									>
										Save
									</Button>
								</>
							) : isDraft ? (
								<>
									<Button
										variant="outlined"
										color="error"
										size="small"
										startIcon={<DeleteOutlineIcon />}
										onClick={() => setConfirm("delete")}
										data-test-id="delete-draft-button"
										disabled={isBusy || rowsLoading}
										sx={{
											backgroundColor: (theme) =>
												theme.palette.background.paper,
										}}
									>
										Delete Draft
									</Button>
									<Button
										variant="outlined"
										size="small"
										startIcon={<EditOutlinedIcon />}
										onClick={startEditing}
										data-test-id="edit-draft-button"
										disabled={isBusy || rowsLoading}
										sx={{
											backgroundColor: (theme) =>
												theme.palette.background.paper,
										}}
									>
										Edit Draft
									</Button>
									<Button
										variant="contained"
										color="success"
										size="small"
										startIcon={<PublishIcon />}
										onClick={() => setConfirmVersionOpen(true)}
										data-test-id="confirm-draft-button"
										disabled={isBusy || rowsLoading}
									>
										Confirm
									</Button>
								</>
							) : isScheduled ? (
								backToDraftButton
							) : hasDraft ? (
								<Button
									variant="outlined"
									size="small"
									startIcon={<VisibilityOutlinedIcon />}
									onClick={() => setSelectedVersionId(draftVersionId)}
									data-test-id="see-draft-button"
									disabled={isBusy}
									sx={{
										backgroundColor: (theme) => theme.palette.background.paper,
									}}
								>
									See Draft
								</Button>
							) : (
								<>
									{isLatestPublished && backToDraftButton}
									{isLatestPublished && canEditPublished && (
										<Tooltip
											title={
												hasDraft
													? "There is already a draft. Edit or delete it before editing the published revision."
													: ""
											}
										>
											<span>
												<Button
													variant="outlined"
													size="small"
													startIcon={<EditOutlinedIcon />}
													onClick={startEditing}
													data-test-id="edit-published-button"
													disabled={isBusy || rowsLoading || hasDraft}
													sx={{
														backgroundColor: (theme) =>
															theme.palette.background.paper,
													}}
												>
													Edit Published
												</Button>
											</span>
										</Tooltip>
									)}
									<Tooltip
										title={
											hasPendingVersion
												? "There is already a scheduled version"
												: ""
										}
									>
										<span data-test-id="new-version-button-wrapper">
											<Button
												variant="contained"
												size="small"
												startIcon={<AddIcon />}
												onClick={() => setNewVersionOpen(true)}
												data-test-id="new-version-button"
												disabled={
													isBusy ||
													rowsLoading ||
													versionsLoading ||
													hasPendingVersion
												}
											>
												New Draft
											</Button>
										</span>
									</Tooltip>
								</>
							)}
						</Box>
					)}
				</Box>

				<Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }} component={Paper}>
					<TensileRequirementsByGradeTable
						rows={displayedRows}
						isLoading={versionsLoading || rowsLoading}
						isEditMode={isEditMode}
						onRowUpdate={handleRowUpdate}
						onRowDelete={handleRowDelete}
					/>
				</Box>

				{isEditMode ? (
					<TextField
						label="Comments / Footnotes"
						slotProps={{
							htmlInput: { "data-test-id": "draft-comments-input" },
						}}
						multiline
						minRows={2}
						maxRows={4}
						size="small"
						fullWidth
						value={editComments}
						onChange={(e) => setEditComments(e.target.value)}
					/>
				) : (
					selectedVersion?.Comments && (
						<Box>
							<Typography variant="body2" color="text.disabled">
								Notes:
							</Typography>
							<Typography
								variant="body2"
								color="text.secondary"
								data-test-id="version-comments"
								sx={{ whiteSpace: "pre-line" }}
							>
								{selectedVersion.Comments}
							</Typography>
						</Box>
					)
				)}
			</Box>

			<NewVersionModal
				open={newVersionOpen}
				isSubmitting={saveMutation.isPending}
				copies="requirements"
				onClose={() => setNewVersionOpen(false)}
				onSubmit={({ comments }) => handleNewVersion(comments)}
			/>

			<ConfirmVersionModal
				open={confirmVersionOpen}
				maxRev={maxRev}
				lastEffectiveDate={lastEffectiveDate}
				lastUsedDate={allVersions?.[0]?.LastTestRunAt ?? null}
				lastUsedLabel="the last day mechanical test results were received"
				allowPastDates
				isSubmitting={confirmMutation.isPending}
				onClose={() => setConfirmVersionOpen(false)}
				onSubmit={({ rev, effectiveDate }) => {
					if (versionId == null) return;
					confirmMutation.mutate({ versionId, rev, effectiveDate });
				}}
			/>

			<ConfirmationDialogRaw
				open={confirm !== null}
				title={
					confirm === "changeToDraft"
						? "Change Revision to Draft"
						: "Delete Draft"
				}
				message={
					confirm !== "changeToDraft"
						? "Delete and discard this draft? Confirmed revisions are unaffected."
						: isScheduled
							? "Change scheduled revision to Draft? Its saved requirements will be preserved. It will no longer become effective automatically."
							: `Change Rev ${selectedVersion?.Rev} to Draft? Its saved requirements will be preserved. The previous revision goes back into force until this one is confirmed again.`
				}
				buttons={{
					no: "Cancel",
					yes: confirm === "changeToDraft" ? "Back to Draft" : "Delete Draft",
				}}
				onClose={handleConfirmClose}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
